<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Progress Indicator -->
    <report-progress :showProgress="isLoading" />

    <!-- Table Container with Sticky Header -->
    <div class="flex-1 overflow-hidden">
      <div class="h-full flex flex-col">
        <!-- Single Table with Sticky Header -->
        <div class="flex-1 overflow-auto">
          <table class="w-full table-fixed text-sm">
            <thead class="sticky top-0 z-10">
              <tr>
                <th
                  class="w-12 !bg-primary-900 text-white set-border text-center font-weight-medium py-2"
                ></th>
                <th
                  class="w-2/3 !bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.repeated_calls_report.head_month") }}
                </th>
                <th
                  class="w-1/3 !bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.repeated_calls_report.head_repeated_calls") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- Month rows (expandable) -->
              <template
                v-for="(monthData, monthIndex) in groupedByMonth"
                :key="monthData.month"
              >
                <tr
                  :class="monthIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  class="cursor-pointer hover:bg-gray-100"
                  @click="toggleMonth(monthData.month)"
                >
                  <td
                    class="text-center text-primary-900 py-2 border-b border-gray-200"
                  >
                    <v-icon size="small" color="primary-900">{{
                      expandedMonths[monthData.month]
                        ? "mdi-minus-circle"
                        : "mdi-plus-circle-outline"
                    }}</v-icon>
                  </td>
                  <td
                    class="text-center text-teal pe-2 py-2 border-b border-gray-200"
                  >
                    <span class="font-weight-bold">{{ monthData.month }}</span>
                  </td>
                  <td
                    class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                  >
                    {{ monthData.totalRepeatedCalls }}
                  </td>
                </tr>
                <!-- Day rows (shown when month is expanded) -->
                <template
                  v-for="dayData in monthData.days"
                  :key="dayData.day"
                >
                  <tr
                    v-show="expandedMonths[monthData.month]"
                    :class="
                      (monthIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white') +
                      ' cursor-pointer hover:bg-gray-100'
                    "
                    @click.stop="toggleDay(monthData.month, dayData.day)"
                  >
                    <td
                      class="text-center text-primary-900 ps-2 py-2 border-b border-gray-200"
                    >
                      <v-icon size="small" color="primary-900">{{
                        expandedDays[`${monthData.month}_${dayData.day}`]
                          ? "mdi-minus-circle"
                          : "mdi-plus-circle-outline"
                      }}</v-icon>
                    </td>
                    <td
                      class="text-center text-teal px-2 py-2 border-b border-gray-200"
                    >
                      <span class="font-weight-medium">{{ dayData.day }}</span>
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ dayData.totalRepeatedCalls }}
                    </td>
                  </tr>
                  <!-- Phone number rows (shown when day is expanded) -->
                  <tr
                    v-for="phoneNumber in dayData.phoneNumbers"
                    v-show="
                      expandedMonths[monthData.month] &&
                      expandedDays[`${monthData.month}_${dayData.day}`]
                    "
                    :key="phoneNumber.number || phoneNumber.phone_number"
                    :class="
                      (monthIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50')
                    "
                  >
                    <td
                      class="text-center text-primary-900 ps-2 py-2 border-b border-gray-200"
                    ></td>
                    <td
                      class="text-center text-grey-darken-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ phoneNumber.number || phoneNumber.phone_number || phoneNumber.phoneNumber }}
                    </td>
                    <td
                      class="text-center text-blue-grey-lighten-1 px-2 py-2 border-b border-gray-200"
                    >
                      {{ phoneNumber.repeated_calls || phoneNumber.repeatedCalls || 0 }}
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportProgress from "../../components/ReportProgress.vue";

export default {
  name: "repeated-calls-report",
  components: {
    ReportProgress,
  },
  props: {
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    reportData: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedMonths: {},
      expandedDays: {},
    };
  },
  methods: {
    extractMonth(item) {
      // Try to extract month from various possible fields
      if (item.month) return item.month;
      if (item.date) {
        const date = new Date(item.date);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          });
        }
      }
      if (item.calldate) {
        const date = new Date(item.calldate);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          });
        }
      }
      // Try to extract YYYY-MM pattern
      const yearMonthMatch = (item.period || item.date || "").match(/(\d{4})-(\d{2})/);
      if (yearMonthMatch) {
        const date = new Date(
          yearMonthMatch[1],
          parseInt(yearMonthMatch[2]) - 1
        );
        return date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      }
      return "Unknown";
    },
    extractDay(item) {
      // Try to extract day from various possible fields
      if (item.day) return item.day;
      if (item.date) {
        const date = new Date(item.date);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        }
      }
      if (item.calldate) {
        const date = new Date(item.calldate);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        }
      }
      // Try to extract YYYY-MM-DD pattern
      const dateMatch = (item.period || item.date || "").match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        const date = new Date(dateMatch[1]);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        }
      }
      return "Unknown";
    },
    toggleMonth(month) {
      this.expandedMonths = {
        ...this.expandedMonths,
        [month]: !this.expandedMonths[month],
      };
    },
    toggleDay(month, day) {
      const key = `${month}_${day}`;
      this.expandedDays = {
        ...this.expandedDays,
        [key]: !this.expandedDays[key],
      };
    },
  },
  computed: {
    groupedByMonth() {
      if (!this.reportData || this.reportData.length === 0) {
        return [];
      }

      const monthGroups = {};

      const parseMonthOrder = (monthKey) => {
        if (!monthKey) return Number.POSITIVE_INFINITY;
        // Try to parse formats like "November" or "November 2025"
        const dateFromName = new Date(`${monthKey} 1, 2000`);
        if (!isNaN(dateFromName.getTime())) {
          return dateFromName.getMonth(); // 0-11
        }
        // Try YYYY-MM
        const ym = monthKey.match(/(\d{4})-(\d{2})/);
        if (ym) {
          return parseInt(ym[2], 10) - 1;
        }
        return Number.POSITIVE_INFINITY;
      };

      const parseDayOrder = (dayKey) => {
        if (!dayKey) return Number.POSITIVE_INFINITY;
        const numeric = parseInt(dayKey, 10);
        if (!isNaN(numeric)) return numeric;
        const dateFromString = new Date(dayKey);
        if (!isNaN(dateFromString.getTime())) {
          return dateFromString.getTime();
        }
        return Number.POSITIVE_INFINITY;
      };

      // First pass: Group by month, then by day, then collect phone numbers
      this.reportData.forEach((item) => {
        const monthKey = this.extractMonth(item);
        const dayKey = this.extractDay(item);

        if (!monthGroups[monthKey]) {
          monthGroups[monthKey] = {
            month: monthKey,
            days: {},
            totalRepeatedCalls: 0,
          };
        }

        if (!monthGroups[monthKey].days[dayKey]) {
          monthGroups[monthKey].days[dayKey] = {
            day: dayKey,
            phoneNumbers: [],
            totalRepeatedCalls: 0,
          };
        }

        // Add phone number data
        const phoneNumber = {
          number: item.number || item.phone_number || item.phoneNumber,
          repeated_calls: parseInt(item.repeated_calls || item.repeatedCalls || 0),
        };

        monthGroups[monthKey].days[dayKey].phoneNumbers.push(phoneNumber);
        monthGroups[monthKey].days[dayKey].totalRepeatedCalls += phoneNumber.repeated_calls;
        monthGroups[monthKey].totalRepeatedCalls += phoneNumber.repeated_calls;
      });

      // Convert days objects to arrays and sort
      Object.values(monthGroups).forEach((monthGroup) => {
        monthGroup.days = Object.values(monthGroup.days).sort((a, b) => {
          return parseDayOrder(a.day) - parseDayOrder(b.day);
        });
      });

      // Convert to array and sort by month
      return Object.values(monthGroups).sort((a, b) => {
        return parseMonthOrder(a.month) - parseMonthOrder(b.month);
      });
    },
  },
};
</script>

<style scoped>
.set-border {
  border: 0.5px solid #d7eaf3;
}
</style>
